import { createContext, useContext, useState, useEffect } from 'react'
import api from '../utils/api'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setLoading(false)
        return
      }

      // Only check auth if we have a token
      const response = await api.get('/auth/me')
      if (response.data.success) {
        setUser(response.data.data.user)
        setIsAuthenticated(true)
      } else {
        localStorage.removeItem('token')
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      // Only clear token if unauthorized (401) or forbidden (403)
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.log('🔒 [AuthContext] auth/me returned 401/403, logging out')
        localStorage.removeItem('token')
        setUser(null)
        setIsAuthenticated(false)
      } else {
        // For other errors (network, 500, etc), don't log out, just stop loading
        console.warn('⚠️ [AuthContext] Auth check failed but token preserved (possible network/server error)')
        // Optionally, you might want to keep the user "logged in" but in a degraded state,
        // or you might want to require a retry. For now, we'll keep the token but user remains null?
        // This is tricky. If we don't have user data, we can't really be "authenticated" in the UI.
        // But logging them out on a network blip is annoying.
        // Let's at least keep the token so a refresh might work later.

        // Ideally, we retry or show an error state.
        // For now, let's NOT call localStorage.removeItem('token').
        setIsAuthenticated(false) // We can't verify identity, so strictly false.
      }
    } finally {
      setLoading(false)
    }
  }

  const loginWithOTP = async (mobile, otp) => {
    try {
      console.log('🔐 [AuthContext] Starting OTP login request...', { mobile })

      const response = await api.post('/auth/otp/verify', { mobile, otp })

      console.log('✅ [AuthContext] OTP login response received:', response.data)

      if (response.data && response.data.success) {
        const { user, token } = response.data.data

        if (!token) {
          console.error('❌ [AuthContext] No token in OTP response')
          return { success: false, message: 'Login failed: No token received' }
        }

        if (!user) {
          console.error('❌ [AuthContext] No user data in OTP response')
          return { success: false, message: 'Login failed: No user data received' }
        }

        console.log('💾 [AuthContext] Storing token and user data')
        localStorage.setItem('token', token)
        setUser(user)
        setIsAuthenticated(true)

        console.log('✅ [AuthContext] OTP login successful')
        return { success: true, user }
      }

      console.warn('⚠️ [AuthContext] OTP login response not successful:', response.data)
      return { success: false, message: response.data?.message || 'OTP login failed' }
    } catch (error) {
      console.error('❌ [AuthContext] OTP login error:', error)

      if (error.response) {
        const errorData = error.response.data
        const errorMessage = errorData?.message ||
          errorData?.errors?.[0]?.msg ||
          `OTP login failed (${error.response.status})`
        return { success: false, message: errorMessage }
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error. Please check if the server is running.'
        }
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred'
        }
      }
    }
  }

  const generateOTP = async (mobile) => {
    try {
      console.log('📱 [AuthContext] Generating OTP for:', mobile)

      const response = await api.post('/auth/otp/generate', { mobile })

      if (response.data && response.data.success) {
        console.log('✅ [AuthContext] OTP generated successfully')
        return {
          success: true,
          message: response.data.message,
          data: response.data.data // May include OTP in development
        }
      }

      return { success: false, message: response.data?.message || 'Failed to generate OTP' }
    } catch (error) {
      console.error('❌ [AuthContext] OTP generation error:', error)

      if (error.response) {
        const errorData = error.response.data
        const errorMessage = errorData?.message ||
          errorData?.errors?.[0]?.msg ||
          `OTP generation failed (${error.response.status})`
        return { success: false, message: errorMessage }
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error. Please check if the server is running.'
        }
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred'
        }
      }
    }
  }

  const login = async (email, password) => {
    try {
      console.log('🔐 [AuthContext] Starting login request...', { email })
      console.log('🔐 [AuthContext] API Base URL:', import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api')

      const response = await api.post('/auth/login', { email, password })

      console.log('✅ [AuthContext] Login response received:', response.data)

      if (response.data && response.data.success) {
        const { user, token } = response.data.data

        if (!token) {
          console.error('❌ [AuthContext] No token in response')
          return { success: false, message: 'Login failed: No token received' }
        }

        if (!user) {
          console.error('❌ [AuthContext] No user data in response')
          return { success: false, message: 'Login failed: No user data received' }
        }

        console.log('💾 [AuthContext] Storing token and user data')
        localStorage.setItem('token', token)
        setUser(user)
        setIsAuthenticated(true)

        console.log('✅ [AuthContext] Login successful')
        return { success: true, user }
      }

      console.warn('⚠️ [AuthContext] Login response not successful:', response.data)
      return { success: false, message: response.data?.message || 'Login failed' }
    } catch (error) {
      console.error('❌ [AuthContext] Login error:', error)
      console.error('❌ [AuthContext] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        request: error.request ? 'Request made but no response' : null
      })

      // Better error handling
      if (error.response) {
        // Server responded with error
        const errorData = error.response.data
        const errorMessage = errorData?.message ||
          errorData?.errors?.[0]?.msg ||
          `Login failed (${error.response.status}). Please check your credentials.`
        return { success: false, message: errorMessage }
      } else if (error.request) {
        // Request made but no response
        console.error('❌ [AuthContext] No response from server')
        return {
          success: false,
          message: 'Network error. Please check if the server is running at http://localhost:3000'
        }
      } else {
        // Something else happened
        console.error('❌ [AuthContext] Unexpected error:', error.message)
        return {
          success: false,
          message: error.message || 'An unexpected error occurred'
        }
      }
    }
  }

  const register = async (name, email, password, mobile = null, otp = null) => {
    try {
      console.log('📝 [AuthContext] Starting registration...', { name, email, mobile: mobile ? 'provided' : 'not provided' })

      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        mobile: mobile || undefined,
        otp: otp || undefined
      })

      console.log('✅ [AuthContext] Registration response received:', response.data)

      if (response.data && response.data.success) {
        const { user, token } = response.data.data

        if (!token) {
          console.error('❌ [AuthContext] No token in registration response')
          return { success: false, message: 'Registration failed: No token received' }
        }

        console.log('💾 [AuthContext] Storing token and user data')
        localStorage.setItem('token', token)
        setUser(user)
        setIsAuthenticated(true)

        console.log('✅ [AuthContext] Registration successful')
        return { success: true, user }
      }

      console.warn('⚠️ [AuthContext] Registration response not successful:', response.data)
      return { success: false, message: response.data?.message || 'Registration failed' }
    } catch (error) {
      console.error('❌ [AuthContext] Registration error:', error)

      if (error.response) {
        const errorData = error.response.data
        const errorMessage = errorData?.message ||
          errorData?.errors?.[0]?.msg ||
          `Registration failed (${error.response.status})`
        return { success: false, message: errorMessage }
      } else if (error.request) {
        return {
          success: false,
          message: 'Network error. Please check if the server is running.'
        }
      } else {
        return {
          success: false,
          message: error.message || 'An unexpected error occurred'
        }
      }
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('token')
      setUser(null)
      setIsAuthenticated(false)
    }
  }

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'owner')
  }

  const isFranchise = () => {
    return user && (user.role === 'franchise' || user.role === 'admin')
  }

  const isTagFranchise = () => {
    return user && (user.role === 'franchise_tags' || user.role === 'admin')
  }

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/me', profileData)
      if (response.data.success) {
        setUser(response.data.data.user)
        return { success: true, user: response.data.data.user }
      }
      return { success: false, message: response.data.message || 'Failed to update profile' }
    } catch (error) {
      console.error('Update profile error:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update profile'
      return { success: false, message: errorMessage }
    }
  }

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithOTP,
    generateOTP,
    register,
    logout,
    updateProfile,
    isAdmin,
    isFranchise,
    isTagFranchise,
    checkAuth
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

